import { Clip } from '@stores/MomentMerge/MomentMergeStore';
import { useCallback, useRef } from 'react';
import { ClipStore } from '../types';

// Constants
const SEEK_DEBOUNCE_DELAY = 50;
const PLAY_AFTER_SEEK_DELAY = 100;
const KEYBOARD_SEEK_THRESHOLD = 5; // If seeking to time less than this via keyboard, go to 0

interface UseScrubToParams {
  store: ClipStore;
  currentClipIndex: number;
  video1Clip: Clip | null;
  video2Clip: Clip | null;
  activeVideo: 'video1' | 'video2';
  video1Ref: React.RefObject<HTMLVideoElement | null>;
  video2Ref: React.RefObject<HTMLVideoElement | null>;
  setCurrentClipIndex: (index: number) => void;
  setActiveVideo: (video: 'video1' | 'video2') => void;
  setVideo1Clip: (clip: Clip | null) => void;
  setVideo2Clip: (clip: Clip | null) => void;
  setVideo1TargetTime: (time: number | undefined) => void;
  setVideo2TargetTime: (time: number | undefined) => void;
  lastVirtualTimestampRef: React.RefObject<number>;
}

export const useScrubTo = ({
  store,
  currentClipIndex,
  video1Clip,
  video2Clip,
  activeVideo,
  video1Ref,
  video2Ref,
  setCurrentClipIndex,
  setActiveVideo,
  setVideo1Clip,
  setVideo2Clip,
  setVideo1TargetTime,
  setVideo2TargetTime,
  lastVirtualTimestampRef,
}: UseScrubToParams) => {
  // Add debouncing to prevent rapid successive seeks
  const seekTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const isSeekingRef = useRef(false);
  // Track the latest seek request to prevent race conditions
  const currentSeekIdRef = useRef(0);

  const scrubTo = useCallback(
    (time: number) => {
      // Generate unique ID for this seek request
      const seekId = ++currentSeekIdRef.current;

      // Validate input
      if (typeof time !== 'number' || isNaN(time) || time < 0) {
        console.warn('Invalid time provided to scrubTo:', time);
        isSeekingRef.current = false;
        return;
      }

      // Ensure time doesn't exceed total duration
      if (time > store.totalClipsDuration) {
        time = store.totalClipsDuration;
      }

      // Clear any pending seek
      if (seekTimeoutRef.current) {
        clearTimeout(seekTimeoutRef.current);
      }

      // If already seeking, queue this seek
      if (isSeekingRef.current) {
        seekTimeoutRef.current = setTimeout(() => {
          // Only execute if this is still the latest seek request
          if (seekId === currentSeekIdRef.current) {
            scrubTo(time);
          }
        }, SEEK_DEBOUNCE_DELAY);
        return;
      }

      isSeekingRef.current = true;

      try {
        const clipInfo = store.clipInTimestamp(time);
        if (!clipInfo) {
          console.warn('No clip info found for time:', time);
          isSeekingRef.current = false;
          return;
        }

        const wasPlaying = store.isPlaying;
        const needsClipChange = clipInfo.currentClipIndex !== currentClipIndex;

        if (needsClipChange) {
          // Don't clear clips - just update them directly to preserve HLS instances
          const newActiveClipIndex = clipInfo.currentClipIndex;
          const newTimeInClip = clipInfo.timeInClip;

          // Validate clip index
          if (newActiveClipIndex < 0 || newActiveClipIndex >= store.clips.length) {
            console.warn('Invalid clip index:', newActiveClipIndex);
            isSeekingRef.current = false;
            return;
          }

          // Update clips directly without clearing
          const activeClip = store.clips[newActiveClipIndex];
          setVideo1Clip(activeClip);
          setVideo1TargetTime(activeClip.startTime + newTimeInClip);
          setCurrentClipIndex(newActiveClipIndex);
          setActiveVideo('video1');

          // Set up the next clip for preloading
          const nextIdx = newActiveClipIndex + 1;
          if (nextIdx < store.clips.length) {
            const nextClip = store.clips[nextIdx];
            setVideo2Clip(nextClip);
            setVideo2TargetTime(nextClip.startTime);
          } else {
            setVideo2Clip(null);
            setVideo2TargetTime(0);
          }

          lastVirtualTimestampRef.current = time;
          store.setCurrentTime(time);

          if (wasPlaying) {
            setTimeout(() => {
              // Only execute if this is still the latest seek request
              if (seekId === currentSeekIdRef.current) {
                const video = video1Ref.current;
                if (video) {
                  store.setIsPlaying(true);
                  video.play().catch((error) => {
                    console.error('Failed to play video after seek:', error);
                    store.setIsPlaying(false);
                  });
                }
                isSeekingRef.current = false;
              }
            }, PLAY_AFTER_SEEK_DELAY);
          } else {
            isSeekingRef.current = false;
          }
        } else {
          // Seeking within the same clip - no HLS recreation needed
          const activeVideoRef = activeVideo === 'video1' ? video1Ref : video2Ref;
          const activeClip = activeVideo === 'video1' ? video1Clip : video2Clip;
          const video = activeVideoRef.current;

          if (video && activeClip) {
            const actualVideoTime = activeClip.startTime + clipInfo.timeInClip;
            video.currentTime = actualVideoTime;
            delete video.dataset.overlapTriggered;

            const previousClipsDuration = store.clips
              .slice(0, clipInfo.currentClipIndex)
              .reduce((total: number, clip: Clip) => total + clip.duration, 0);
            const newVirtualTimestamp = previousClipsDuration + clipInfo.timeInClip;
            lastVirtualTimestampRef.current = newVirtualTimestamp;
            store.setCurrentTime(newVirtualTimestamp);

            if (wasPlaying && video.paused) {
              video.play().catch((error) => {
                console.error('Failed to play video after seek:', error);
                store.setIsPlaying(false);
              });
            }
          }
          isSeekingRef.current = false;
        }
      } catch (error) {
        console.error('Error during scrubTo:', error);
        // Only reset seeking state if this is still the latest seek request
        if (seekId === currentSeekIdRef.current) {
          isSeekingRef.current = false;
        }
      }
    },
    [
      store,
      currentClipIndex,
      video1Clip,
      video2Clip,
      video1Ref,
      video2Ref,
      setCurrentClipIndex,
      setActiveVideo,
      setVideo1Clip,
      setVideo2Clip,
      setVideo1TargetTime,
      setVideo2TargetTime,
      lastVirtualTimestampRef,
    ],
  );

  const scrubToWithKeyboardBounds = useCallback(
    (time: number) => {
      // Apply keyboard-specific bounds checking
      if (time < KEYBOARD_SEEK_THRESHOLD) {
        time = 0;
      }

      // Use the main scrubTo function
      scrubTo(time);
    },
    [scrubTo],
  );

  return {
    scrubTo,
    scrubToWithKeyboardBounds,
  };
};
